import { useRef, useState, useEffect, useCallback } from 'react'
import styles from './HeroScene.module.css'

interface Vec2 { x: number; y: number }

function lerp(a: number, b: number, t: number) { return a + (b - a) * t }

const R = 160
const STRIP = R * 4

export default function HeroScene() {
  const ref = useRef<HTMLDivElement>(null)
  const target = useRef<Vec2>({ x: 250, y: 250 })
  const current = useRef<Vec2>({ x: 250, y: 250 })
  const [off, setOff] = useState<Vec2>({ x: 0, y: 0 })
  const raf = useRef(0)

  const onMove = useCallback((e: React.MouseEvent) => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    target.current = {
      x: ((e.clientX - r.left) / r.width - 0.5) * 2,
      y: ((e.clientY - r.top) / r.height - 0.5) * 2,
    }
  }, [])

  const onLeave = useCallback(() => { target.current = { x: 0, y: 0 } }, [])

  useEffect(() => {
    const tick = () => {
      const c = current.current, t = target.current
      c.x = lerp(c.x, t.x, 0.03)
      c.y = lerp(c.y, t.y, 0.03)
      setOff({ x: c.x * 6, y: c.y * 4 })
      raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  const cx = 250 + off.x
  const cy = 248 + off.y

  const continents = (
    <>
      {/* North America */}
      <path d={`M${cx - 210} ${cy - 40}
        q-5,-18 8,-40
        q12,-18 28,-26
        q18,-6 34,-3
        q20,4 30,14
        q8,8 5,22
        q-3,12 -6,20
        q-3,8 1,14
        q4,6 -2,10
        q-8,3 -16,0
        q-10,-4 -18,2
        q-8,6 -14,12
        q-8,5 -14,10
        q-5,-5 -10,-12
        q-5,-12 -8,-22
        q-2,-8 -4,-13z`}
        fill="#14B8A6" />
      {/* Greenland */}
      <ellipse cx={cx - 138} cy={cy - 116}
        rx="14" ry="18" fill="#14B8A6" opacity="0.7"
        transform={`rotate(-12, ${cx - 138}, ${cy - 116})`} />
      {/* South America */}
      <path d={`M${cx - 128} ${cy + 12}
        q14,-8 28,-3
        q12,8 16,24
        q5,18 3,35
        q-2,18 -8,32
        q-6,14 -16,20
        q-8,2 -12,-6
        q-3,-12 -5,-26
        q-2,-16 0,-30
        q1,-14 -1,-24
        q-2,-10 -5,-18z`}
        fill="#0D9488" />
      {/* Europe */}
      <path d={`M${cx + 14} ${cy - 92}
        q6,-4 14,-2
        q8,3 14,9
        q4,6 2,14
        q-3,8 -10,10
        q-8,2 -14,-2
        q-6,-4 -8,-10
        q-2,-6 0,-12
        q2,-4 2,-7z`}
        fill="#14B8A6" />
      {/* British Isles */}
      <ellipse cx={cx + 8} cy={cy - 100}
        rx="4" ry="7" fill="#14B8A6" opacity="0.65"
        transform={`rotate(-8, ${cx + 8}, ${cy - 100})`} />
      {/* Scandinavia */}
      <path d={`M${cx + 38} ${cy - 110}
        q3,-5 8,-8
        q5,-2 8,4
        q2,7 0,15
        q-3,6 -7,4
        q-5,-3 -7,-8
        q-1,-4 -2,-7z`}
        fill="#14B8A6" opacity="0.65" />
      {/* Africa */}
      <path d={`M${cx + 16} ${cy - 60}
        q12,-8 28,-3
        q14,5 22,16
        q6,10 8,24
        q2,14 0,30
        q-2,16 -6,30
        q-5,14 -15,22
        q-10,8 -20,4
        q-8,-5 -12,-16
        q-4,-12 -5,-26
        q-1,-16 1,-30
        q2,-14 5,-26
        q2,-10 -2,-18z`}
        fill="#0D9488" />
      {/* Madagascar */}
      <ellipse cx={cx + 70} cy={cy + 48}
        rx="4" ry="11" fill="#0D9488" opacity="0.5"
        transform={`rotate(-15, ${cx + 70}, ${cy + 48})`} />
      {/* Arabian Peninsula */}
      <path d={`M${cx + 92} ${cy - 48}
        q8,-3 15,2
        q5,6 3,16
        q-3,8 -10,10
        q-7,0 -10,-6
        q-2,-8 0,-14
        q1,-4 2,-8z`}
        fill="#14B8A6" opacity="0.55" />
      {/* India */}
      <path d={`M${cx + 135} ${cy - 32}
        q10,-6 18,0
        q6,8 5,20
        q-2,12 -10,16
        q-8,2 -13,-6
        q-4,-10 -2,-18
        q1,-6 2,-12z`}
        fill="#14B8A6" opacity="0.5" />
      {/* Southeast Asia */}
      <path d={`M${cx + 158} ${cy - 20}
        q6,-3 12,0
        q4,4 2,10
        q-3,5 -8,4
        q-5,-2 -6,-6
        q-1,-4 0,-8z`}
        fill="#0D9488" opacity="0.35" />
      {/* Asia mainland */}
      <path d={`M${cx + 130} ${cy - 100}
        q18,-10 42,-8
        q24,4 36,18
        q10,14 4,30
        q-8,16 -16,24
        q-10,5 -22,0
        q-14,-6 -22,-16
        q-8,-12 -12,-22
        q-4,-10 -6,-16
        q-2,-5 -4,-10z`}
        fill="#14B8A6" />
      {/* Japan */}
      <ellipse cx={cx + 248} cy={cy - 68}
        rx="4" ry="12" fill="#14B8A6" opacity="0.45"
        transform={`rotate(15, ${cx + 248}, ${cy - 68})`} />
      {/* Australia */}
      <path d={`M${cx + 218} ${cy + 38}
        q16,-8 36,-4
        q14,6 16,20
        q0,14 -12,18
        q-14,5 -28,0
        q-12,-5 -16,-14
        q-4,-10 4,-20z`}
        fill="#0D9488" />
      {/* New Zealand */}
      <ellipse cx={cx + 272} cy={cy + 68}
        rx="3" ry="8" fill="#0D9488" opacity="0.35"
        transform={`rotate(-25, ${cx + 272}, ${cy + 68})`} />
      {/* Antarctica hint */}
      <path d={`M${cx - 200} ${cy + 138}
        q60,8 120,10 q60,2 120,-2 q60,-4 120,-10
        q-40,10 -100,12 q-60,2 -120,0 q-60,-2 -100,-5 q-20,-2 -40,-5z`}
        fill="#14B8A6" opacity="0.15" />
    </>
  )

  return (
    <div ref={ref} className={styles.canvas} onMouseMove={onMove} onMouseLeave={onLeave}>
      <svg viewBox="0 0 500 500" fill="none" className={styles.illustration}>
        <defs>
          <radialGradient id="earthFill" cx="0.42" cy="0.38" r="0.58">
            <stop offset="0%" stopColor="#1E3A5F" />
            <stop offset="100%" stopColor="#0F1F33" />
          </radialGradient>
          <radialGradient id="atmo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="82%" stopColor="transparent" />
            <stop offset="95%" stopColor="#14B8A6" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="earthHL" cx="0.35" cy="0.3" r="0.4">
            <stop offset="0%" stopColor="white" stopOpacity="0.07" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="amb" cx="0.5" cy="0.48" r="0.5">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.03" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <clipPath id="earthClip">
            <circle cx={cx} cy={cy} r={R} />
          </clipPath>
        </defs>

        {/* Ambient glow */}
        <circle cx={cx} cy={cy} r="280" fill="url(#amb)" className={styles.breathe} />

        {/* Orbit paths */}
        <ellipse cx={cx} cy={cy} rx="235" ry="78" fill="none"
          stroke="white" strokeWidth="0.4" opacity="0.05"
          transform={`rotate(-18, ${cx}, ${cy})`} />
        <ellipse cx={cx} cy={cy} rx="265" ry="58" fill="none"
          stroke="white" strokeWidth="0.3" opacity="0.035"
          transform={`rotate(8, ${cx}, ${cy})`} />

        {/* Earth */}
        <g className={styles.earthEntry}>
          <circle cx={cx} cy={cy} r={R} fill="url(#earthFill)" />

          {/* Continents — horizontal scroll for globe spin */}
          <g clipPath="url(#earthClip)">
            <g className={styles.globeSpin} opacity="0.35">
              {continents}
              <g transform={`translate(${STRIP}, 0)`}>
                {continents}
              </g>
            </g>
          </g>

          {/* Meridian grid */}
          <g clipPath="url(#earthClip)" opacity="0.04" stroke="white" strokeWidth="0.5" fill="none">
            <ellipse cx={cx} cy={cy} rx={R} ry={R} />
            <ellipse cx={cx} cy={cy} rx={R * 0.67} ry={R} />
            <ellipse cx={cx} cy={cy} rx={R * 0.29} ry={R} />
            <line x1={cx - R} y1={cy} x2={cx + R} y2={cy} />
            <line x1={cx - R * 0.95} y1={cy - R * 0.33} x2={cx + R * 0.95} y2={cy - R * 0.33} />
            <line x1={cx - R * 0.95} y1={cy + R * 0.33} x2={cx + R * 0.95} y2={cy + R * 0.33} />
          </g>

          {/* Specular highlight */}
          <circle cx={cx} cy={cy} r={R} fill="url(#earthHL)" />

          {/* Atmosphere */}
          <circle cx={cx} cy={cy} r={R + 10} fill="url(#atmo)" />

          {/* Rim */}
          <circle cx={cx} cy={cy} r={R} fill="none" stroke="white" strokeWidth="0.5" opacity="0.06" />
        </g>

        {/* Satellite 1 */}
        <g className={styles.sat1} style={{ transformOrigin: `${cx}px ${cy}px` }}>
          <g transform={`translate(${cx}, ${cy})`}>
            <g transform="rotate(-18) translate(235, 0) rotate(18)">
              <rect x="-9" y="-5" width="18" height="10" rx="2.5" fill="#64748B" opacity="0.8" />
              <rect x="-24" y="-3" width="14" height="6" rx="1.2" fill="#0D9488" opacity="0.5" />
              <rect x="10" y="-3" width="14" height="6" rx="1.2" fill="#0D9488" opacity="0.5" />
              <circle r="2.2" cy="-7" fill="#D97706" opacity="0.6" className={styles.blink} />
            </g>
          </g>
        </g>

        {/* Satellite 2 */}
        <g className={styles.sat2} style={{ transformOrigin: `${cx}px ${cy}px` }}>
          <g transform={`translate(${cx}, ${cy})`}>
            <g transform="rotate(8) translate(265, 0) rotate(-8)">
              <rect x="-7" y="-4" width="14" height="8" rx="2" fill="#64748B" opacity="0.65" />
              <rect x="-18" y="-2.5" width="10" height="5" rx="1" fill="#0D9488" opacity="0.4" />
              <rect x="8" y="-2.5" width="10" height="5" rx="1" fill="#0D9488" opacity="0.4" />
              <circle r="1.8" cy="-6" fill="#D97706" opacity="0.5" className={styles.blink2} />
            </g>
          </g>
        </g>

        {/* Stars */}
        <circle cx="62" cy="78" r="0.8" fill="white" opacity="0.06" />
        <circle cx="440" cy="65" r="0.6" fill="white" opacity="0.04" />
        <circle cx="50" cy="400" r="0.7" fill="white" opacity="0.04" />
        <circle cx="450" cy="420" r="0.8" fill="white" opacity="0.05" />
        <circle cx="430" cy="195" r="0.5" fill="white" opacity="0.03" />
        <circle cx="78" cy="260" r="0.6" fill="white" opacity="0.035" />
        <circle cx="390" cy="350" r="0.5" fill="white" opacity="0.03" />
        <circle cx="120" cy="130" r="0.7" fill="white" opacity="0.04" className={styles.twinkle} />
        <circle cx="410" cy="145" r="0.6" fill="white" opacity="0.035" className={styles.twinkle2} />
      </svg>
    </div>
  )
}
